import React, { useEffect } from 'react';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, Divider, FormControl, FormControlLabel, FormHelperText, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import { AddNewUserBody, addNewUser, handleToggleAddNewUserDialog } from '../../store/usermanagement-slice/usermanagement-slice';
import { enqueueSnackbar } from '../../store/app-slice/app';
import { handleNotificationVariant } from '../notifications/_common';
import { AppRoutes } from '../../axios/endPoints';
import { Header } from '../common/Header';
import { MLRoles } from '../layout/menu-items';
import Transition from '../common/Transition';

interface AddNewUserForm {
  email: string,
  role: string,
  trialSubscription: boolean,
  subscriptionEndDate?: string,
  modelsAllowedEnabled: boolean,
  modelsAllowed?: number
}

const formatDateInput = (value: moment.MomentInput) => moment(value).format('YYYY-MM-DD');

const dateInputToUtcIso = (value: string) => moment.utc(value, 'YYYY-MM-DD').startOf('day').toISOString();

const dateInputAfterToday = (value?: string) => {
  if(!value) return false;
  return moment(value, 'YYYY-MM-DD').isAfter(moment().startOf('day'), 'day');
};

const addUserDefaultValues: AddNewUserForm = {
  email: '',
  role: '',
  trialSubscription: false,
  subscriptionEndDate: '',
  modelsAllowedEnabled: false,
  modelsAllowed: undefined
};

const schema = (t : Function) => yup.object().shape({
  email: yup.string().email(t('USER_MANAGEMENT_EMAIL_VALIDATION')).required(t('USER_MANAGEMENT_EMAIL_VALIDATION')),
  role: yup.string().required(t('USER_MANAGEMENT_ROLE_VALIDATION')),
  trialSubscription: yup.boolean().default(false),
  subscriptionEndDate: yup.string().when('trialSubscription', ([trialSubscription], schema) => {
    return trialSubscription === true
      ? schema
          .required(t('USER_MANAGEMENT_TRIAL_END_REQUIRED'))
          .test('after-today', t('USER_MANAGEMENT_TRIAL_END_AFTER_TODAY'), (value) => dateInputAfterToday(value ?? undefined))
      : schema.nullable();
  }),
  modelsAllowedEnabled: yup.boolean().default(false),
  modelsAllowed: yup.number()
    .transform((value, originalValue) => originalValue === '' || originalValue === null ? undefined : value)
    .when(['trialSubscription', 'modelsAllowedEnabled'], ([trialSubscription, modelsAllowedEnabled], schema) => {
      return trialSubscription && modelsAllowedEnabled
        ? schema
            .typeError(t('USER_MANAGEMENT_MODELS_ALLOWED_REQUIRED'))
            .min(0, t('USER_MANAGEMENT_MODELS_ALLOWED_MIN'))
            .required(t('USER_MANAGEMENT_MODELS_ALLOWED_REQUIRED'))
        : schema.nullable();
    })
});

export const AddNewUserDialog = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { addNewUserDialog } = useAppSelector((state: RootState) => state.usermanagement);
  const { control, resetField, handleSubmit, formState: { errors }, watch, reset } = useForm<AddNewUserForm>({  
    mode: "onBlur", 
    resolver: yupResolver(schema(t)),
    defaultValues: addUserDefaultValues
});

  const watchTrialSubscription = watch('trialSubscription');
  const watchModelsAllowedEnabled = watch('modelsAllowedEnabled');

  const todayDateString = formatDateInput(new Date());
  const minEndDateString = formatDateInput(moment().add(1, 'day'));

  function handleClose() {
    reset(addUserDefaultValues);
    dispatch(handleToggleAddNewUserDialog(false));
  }

  function handleAddNewUser(data: AddNewUserForm) 
  {
    const changePasswordUrl = new URL(
      AppRoutes.RESET_PASSWORD,
      window.location.protocol + '//' + window.location.host
    ).href;

    const payload: AddNewUserBody = {
      email: data.email,
      role: data.role,
      changePasswordUrl: changePasswordUrl
    };

    if(data.trialSubscription && data.subscriptionEndDate) {
      payload.subscription = {
        username: data.email,
        subscriptionName: "BE_MEDML_SUBSCRIPTION_TRIAL_NAME",
        startDate: dateInputToUtcIso(todayDateString),
        endDate: dateInputToUtcIso(data.subscriptionEndDate),
        modelsAllowed: data.modelsAllowedEnabled ? Number(data.modelsAllowed) : null
      }
    }
    dispatch(addNewUser(payload)).then((res : any) => {
        if(res && res.payload) { 
          dispatch(enqueueSnackbar({ 
              message: t(`${res.payload.message}`), 
              options: { 
                  variant: handleNotificationVariant(res.payload.success),
                  persist: !res.payload.success
              } 
          }))
        }
      }
    );
  }

  useEffect(() => {
    if(!addNewUserDialog) {
      reset(addUserDefaultValues);
    }
  }, [addNewUserDialog, reset]);

  useEffect(() => {
    if (!watchModelsAllowedEnabled) {
      resetField('modelsAllowed')
    }
  }, [watchModelsAllowedEnabled, resetField]);

  return(
    <Dialog
      fullWidth
      maxWidth="sm"
      TransitionComponent={Transition} 
      open={addNewUserDialog} 
    >
      <form onSubmit={handleSubmit(handleAddNewUser)}>
        <Header title={t('USER_MANAGEMENT_ADD_NEW_USER')} />
        <DialogContent>
          <DialogContentText>
            {t('USER_MANAGEMENT_ADD_USER_INFO_TEXT')}
          </DialogContentText>
          <br/>
          <Controller control={control} name="email" render={({field, fieldState: { error }}) => 
            ( 
              <TextField 
                {...field} 
                fullWidth 
                variant="outlined" 
                label={t('USER_MANAGEMENT_EMAIL_LABEL')} 
                type="text"
                error={error !== undefined}
                helperText={error ? errors.email?.message : ''}
              />
            )}
          />
          <Controller control={control} name="role" render={({ field,  fieldState: { error }}) => (
            <FormControl style={{ marginTop: '20px' }} fullWidth error={error !== undefined}>
                <InputLabel id="role-label">{t('USER_MANAGEMENT_ROLE_LABEL')}</InputLabel>
                <Select
                  labelId="role-label"
                  label={t('USER_MANAGEMENT_ROLE_LABEL')}
                  fullWidth
                  variant="outlined"
                  {...field} 
                >
                  {Object.values(MLRoles)?.map((value) => {
                    return(<MenuItem key={value} value={value}>{value}</MenuItem>)
                  })}
                </Select>
                { errors ? <FormHelperText>{errors.role?.message}</FormHelperText> : ''}
            </FormControl>
          )}
          />
          <Box sx={{ mt: 2.5 }}>
            <Divider />
            
            <Controller 
              control={control} 
              name="trialSubscription" 
              render={({ field }) => (
                <FormControlLabel 
                  sx={{ pb: 1, pt: 1.5 }}
                  control={<Checkbox {...field} checked={field.value} />} 
                  label={t('USER_MANAGEMENT_TRIAL_SUBSCRIPTION_LABEL')}
                />
              )}
            />
            
            {watchTrialSubscription && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      value={todayDateString}
                      fullWidth
                      variant="outlined"
                      label={t('USER_MANAGEMENT_TRIAL_START_LABEL')}
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      disabled
                      size='small'
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Controller 
                      control={control} 
                      name="subscriptionEndDate" 
                      render={({ field, fieldState: { error } }) => (
                        <TextField 
                          {...field} 
                          fullWidth 
                          variant="outlined" 
                          label={t('USER_MANAGEMENT_TRIAL_END_LABEL')} 
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ min: minEndDateString }}
                          error={!!error}
                          helperText={error?.message}
                          size='small'
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                
                <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <Controller 
                      control={control} 
                      name="modelsAllowedEnabled" 
                      render={({ field }) => (
                        <FormControlLabel 
                          control={<Checkbox {...field} checked={field.value} />} 
                          label={t('USER_MANAGEMENT_MODELS_ALLOWED_LABEL')}
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Controller 
                      control={control} 
                      name="modelsAllowed" 
                      render={({ field, fieldState: { error } }) => (
                        <TextField 
                          {...field}
                          value={field.value ?? ''} 
                          fullWidth 
                          variant="outlined" 
                          label={t('USER_MANAGEMENT_MODELS_ALLOWED_LABEL')} 
                          type="number"
                          inputProps={{ min: 0 }}
                          disabled={!watchModelsAllowedEnabled}
                          error={!!error}
                          helperText={error?.message}
                          size='small'
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button className="button-text-color" onClick={handleClose}>{t('CANCEL_BUTTON')}</Button>
          <Button className="button-text-color" type="submit">{t('SUBMIT_BUTTON')}</Button>
        </DialogActions>
      </form>
    </Dialog>
  )
};
